<template>
    <div>
        <div class="row">
            <div class="col-md-6">
                <form class="form-horizontal">
                    <div class="form-group">
                        <div class="col-sm-3">
                            <select v-model="perPage"
                                    class="form-control"
                                    id="perPageSelect">
                                <option v-for="pageOption in pageOptions" :value="pageOption">{{ pageOption }}</option>
                            </select>
                        </div>
                    </div>
                </form>
            </div>
            <div class="col-md-6">
                <b-pagination
                    class="pull-right margin"
                    v-model="currentPage"
                    :total-rows="rows"
                    :per-page="perPage"
                    align="right">
                </b-pagination>
            </div>
        </div>

        <b-table
            responsive
            :filter="searchQuery"
            ref="participantsTable"
            sort-icon-left
            :fields="fields"
            :current-page="currentPage"
            :per-page="perPage"
            :sort-desc="true"
            sort-by="Participants.completed"
            :items="participantProvider">

            <template v-slot:HEAD_bulk="data">
                <b-form-checkbox
                    @change="selectAll"
                    name="select-all">
                </b-form-checkbox>
            </template>

            <template v-slot:bulk="{item}">
                <b-form-checkbox
                    :id="`participat-${item.id}`"
                    v-model="internallySelected"
                    :name="`participat-${item.id}`"
                    @change="clicked"
                    :value="item.id"
                    :unchecked-value="item.id">
                </b-form-checkbox>
            </template>

            <template v-slot:action="{item}">
                <a class="btn btn-xs btn-success"
                   target="_blank"
                   :href="'/admin/participants/view/'+item['id']"
                >
                    Manage
                </a>
                <template v-if="['waiting_list_invited', 'waiting_list_expired'].includes(item[`Participants.participant_status`])">
                    <waiting-list-action-modal
                        :participant="item"
                        :modal-id="`waitingListExpiredActionsModal-${item.id}`"
                        @success="handleWaitingListActionSuccess"
                    ></waiting-list-action-modal>
                    <button type="button" class="btn btn-xs btn-info" data-toggle="modal" :data-target="`#waitingListExpiredActionsModal-${item.id}`">
                        Re-invite/Rejoin
                    </button>
                </template>
                <api-post-button
                    v-if="item[`Participants.participant_status`] === `waiting_list_invited`"
                    :end-point="`/api/v0/participants/expire_waiting_list_invite/${item.id}`"
                    method="put"
                    text="Expire"
                    classes="btn btn-xs btn-danger"
                    authentication-type="jwt"
                    confirm="Are you sure you want to expire this participant?"
                    @response="refreshTable"
                ></api-post-button>
                <api-post-button
                    v-if="item[`Participants.participant_status`] === `waiting_list_joined`"
                    :end-point="`/api/v0/participants/invite/${item.id}`"
                    method="put"
                    text="Invite"
                    classes="btn btn-xs btn-info"
                    authentication-type="jwt"
                    confirm="Are you sure you want to invite this participant?"
                    @response="refreshTable"
                ></api-post-button>
            </template>
            <template v-slot:Participants.waiting_list_joined="{item}">
                <i v-if="item['TransferFromParticipants.id']" class="fa fa-exchange" data-toggle="tooltip" :title="`Offered a transfer by Participant with id: ${item['TransferFromParticipants.id']}`"></i>
                <span>{{item[`Participants.waiting_list_joined`] | dateTimeFormat(item[`Occasions.timezone`], 'DD/MM/YY HH:mm')}}</span>
            </template>
            <template v-slot:Participants.completed="{item}">
                <i v-if="item['Participants.fundraising']" class="fas fa-hand-holding-medical text-success"
                   data-toggle="tooltip" title="This participant has setup a fundraising page"></i>
                <i v-if="['prepay_uninvited', 'prepay_invited'].includes(item['Participants.participant_status'])" class="fas fa-exclamation-triangle text-danger"
                   data-toggle="tooltip" title="This entry has been pre-paid but not yet been claimed and confirmed"></i>
                <span v-if="!['prepay_uninvited', 'prepay_invited'].includes(item['Participants.participant_status'])" v-html="item['Participants.completed']"></span>
            </template>
            <template v-slot:Participants.id="{item}">
                <span v-html="item['Participants.id']"></span>
            </template>
            <template v-slot:Participants.first_name="{item}">
                <i v-if="item['Participants.checked_in']" class="fas fa-check-circle text-success" data-toggle="tooltip"
                   title="This participant has checked in!"></i>
                {{ item['Participants.first_name'] }}
            </template>
            <template v-slot:Participants.waiting_list_invite_expiry="{item}">
                <span v-html="item['Participants.waiting_list_invite_expiry']"></span>
            </template>
            <template v-slot:Teams.name="{item}">
                <i v-if="item['Teams.captain']" class="fas fa-medal text-warning"></i>
                {{ item['Teams.name'] }}
            </template>
        </b-table>
    </div>
</template>
<script>
import WaitingListActionModal from "./WaitingListActionModal.vue";
import ApiPostButton from "../Utility/Buttons/ApiPostButton.vue";
import {dateTimeFormat} from '../Utility/filters/dates'

export default {
    components: {
        WaitingListActionModal,
        ApiPostButton,
    },
    props: {
        occasionId: {
            type: String
        },
        searchQuery: {
            type: String
        },
        reportKey: {
            type: String,
            default: "",
            required: false
        },
        reportType: {
            type: String,
            required: false,
        },
        value: { //v-model for selected ID's
            type: Array,
            default: () => []
        }
    },
    filters: {
        dateTimeFormat,
    },
    data() {
        return {
            fields: [], // basic default fields for front end
            loading: false,
            perPage: 50,
            currentPage: 1,
            pageOptions: [50, 100, 150, 200],
            rows: 0,
            query: '',
            internallySelected: [],
            inViewIds: [] //the list of ID's currently on display to the user - used for selecting all checkboxes
        }
    },
    computed: {
        frontEndBoolean() {
            return this.frontEnd ? 1 : 0;
        },
    },
    watch: {
        value: {
            handler(val) {
                this.internallySelected = val
            },
            immediate: true
        },
        inViewIds() {
            this.$emit('updated', {inViewIds: this.inViewIds, rows: this.rows});
        },
        perPage() {
            this.currentPage = 1;
        }
    },
    methods: {

        async participantProvider(ctx, callback) {
            let
                urlParams = new URLSearchParams(window.location.search),
                endPoint = `/api/v0/occasions/${this.occasionId}/participants?${ctx.filter}&sortBy=${ctx.sortBy}&sortDesc=${ctx.sortDesc}&page=${ctx.currentPage}&limit=${ctx.perPage}&reportId=${this.reportKey}`
            ;

            if (this.reportType) {
                endPoint = `${endPoint}&reportType=${this.reportType}`;
            }
            let response;
            if (this.reportKey) {
                // Shared report — backend uses reportId (access_key) for auth, no JWT needed
                response = await axios.get(endPoint);
            } else {
                response = await this.$root.makeApiRequest(async (jwtToken) => {
                    return await axios.get(endPoint, {
                        headers: { 'Authorization': jwtToken }
                    });
                });
            }
            let items = response.data.data.data;

            let ids = items.map(a => a.id);
            this.inViewIds = ids;

            const event = new CustomEvent('participant_count', {
                detail: {
                    count: items.length
                }
            });
            event.initEvent('participant_count', true, true);
            window.dispatchEvent(event);
            this.fields = response.data.data.headers;

            this.rows = response.data.pagination.count;
            this.perPage = response.data.pagination.limit;

            if (response.data.pagination.page > response.data.pagination.pages) {
                this.currentPage = 1;
            } else {
                this.currentPage = response.data.pagination.page;
            }

            if (!this.reportKey) {
                this.fields.push({key: 'action', label: 'Action'});
                this.fields.unshift({key: 'bulk', label: ''});
            }

            return items || []
        },
        selectAll(checked) {
            if (checked) {
                this.$emit('selectall', this.inViewIds);
            } else {
                this.$emit('selectall', []);
            }
        },
        clicked(item) {
            Vue.nextTick(() => {
                this.$emit('input', this.internallySelected)
            })
        },
        refreshTable() {
            this.$refs.participantsTable.refresh();
        },
        handleWaitingListActionSuccess(data) {
            // Delay table refresh to allow notification to appear first
            setTimeout(() => {
                this.refreshTable();
            }, 1000);
        },
        reInvitedAction(message) {
            alert(message)
            this.refreshTable()
        }
    }
}
</script>
<style src='bootstrap-vue/dist/bootstrap-vue.css'></style>




// WEBPACK FOOTER //
// src/Assets/components/Participants/Table.vue